import { useEffect, useState } from 'react'
import { ChevronDown, ChevronUp, SlidersHorizontal, Info } from 'lucide-react'
import { DEFAULT_FRAUD_SIGNALS } from '../data'
import type { FraudSignalConfig } from '../types'
import { applyIdSuggestions, useAiSeed } from '../aiConfigure'
import type { AiIdSuggestion, AiSeed } from '../aiConfigure'

interface FraudConfigPanelProps {
  onSignalsChange: (signals: FraudSignalConfig[]) => void
  /** AI Configure from Scope — applied once the rep accepts the suggestion. */
  aiSeed?: AiSeed<AiIdSuggestion[]> | null
}

export function FraudConfigPanel({ onSignalsChange, aiSeed }: FraudConfigPanelProps) {
  const [open, setOpen] = useState(true)
  const [signals, setSignals] = useState<FraudSignalConfig[]>(DEFAULT_FRAUD_SIGNALS)

  useAiSeed(aiSeed, (suggestions) => setSignals((prev) => applyIdSuggestions(prev, suggestions)))

  useEffect(() => {
    onSignalsChange(signals)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signals])

  function toggleSignal(id: string) {
    setSignals((prev) => prev.map((s) => (s.id === id ? { ...s, enabled: !s.enabled } : s)))
  }

  return (
    <div className="rounded-[14px] overflow-hidden" style={{ border: '1px solid #e5e7eb', background: '#ffffff' }}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-2.5 transition-colors hover:bg-[#ffffff]"
        style={{ background: '#f8fafc' }}
      >
        <span className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-[#64748b]">
          <SlidersHorizontal size={12} className="text-[#4f46e5]" />
          Fraud & Device Risk Configuration
        </span>
        {open ? <ChevronUp size={12} className="text-[#64748b]" /> : <ChevronDown size={12} className="text-[#64748b]" />}
      </button>

      {open && (
        <div className="p-4 space-y-3">
          <p className="text-[10px] text-[#475569] flex items-start gap-1.5">
            <Info size={11} className="shrink-0 mt-0.5" />
            These signals evaluate together as part of the workflow&apos;s fraud step — the toggles below are a reference for which ones are in scope for this customer, not independent live switches.
          </p>

          {signals.map((signal) => (
            <div
              key={signal.id}
              className="flex items-center gap-3 px-3 py-2.5 rounded-[9px]"
              style={signal.enabled ? { background: '#f8fafc', border: '1px solid #e5e7eb' } : { background: '#f8fafc', border: '1px solid #e5e7eb', opacity: 0.55 }}
            >
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-semibold text-[#1e293b]">{signal.label}</p>
                <p className="text-[10px] text-[#64748b] mt-0.5 leading-snug">{signal.description}</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer shrink-0">
                <input type="checkbox" checked={signal.enabled} onChange={() => toggleSignal(signal.id)} className="sr-only peer" />
                <div className="w-8 h-[18px] rounded-full transition-colors" style={{ background: signal.enabled ? '#6366f1' : '#e5e7eb' }} />
                <div className="absolute left-[3px] top-[3px] w-3 h-3 rounded-full bg-white transition-transform" style={{ transform: signal.enabled ? 'translateX(14px)' : 'translateX(0)' }} />
              </label>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
